import { useEffect, useState, useRef } from "react";
import { BarChart3, Loader2, Play, RefreshCw } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { ValidationBanner, ValidationChip, useValidationStatus } from "@/components/Validation";
import { useTradingStyle } from "@/contexts/TradingStyleContext";
import { api } from "@/lib/api";

function fmt(v, d = 2) { return v == null ? "—" : Number(v).toFixed(d); }
function fmtPct(v) { return v == null ? "—" : `${Number(v).toFixed(2)}%`; }
function dirCls(v) { return v == null ? "text-slate-400" : v > 0 ? "text-emerald-300" : v < 0 ? "text-red-300" : "text-slate-400"; }

function Stat({ label, value, tone = "slate", testid }) {
  const cls = {
    emerald: "text-emerald-300",
    red: "text-red-300",
    amber: "text-amber-300",
    slate: "text-slate-100",
    cyan: "text-cyan-300",
  }[tone] || "text-slate-100";
  return (
    <div data-testid={testid} className="border border-[#1E293B] bg-[#0F1626] px-3 py-3">
      <div className="mono text-[10px] uppercase tracking-widest text-slate-500 leading-none">{label}</div>
      <div className={`mono text-xl font-bold tabular-nums mt-1.5 leading-none ${cls}`}>{value}</div>
    </div>
  );
}

function EquityCurve({ points }) {
  if (!points || points.length < 2) {
    return <div className="mono text-[11px] text-slate-500 p-6 text-center">Not enough data.</div>;
  }
  const w = 800, h = 160;
  const xs = points.map((p, i) => i);
  const ys = points.map((p) => p.cum_r);
  const minY = Math.min(...ys, 0);
  const maxY = Math.max(...ys, 0);
  const rangeY = maxY - minY || 1;
  const rangeX = xs.length - 1 || 1;
  const path = points.map((p, i) => {
    const x = (i / rangeX) * w;
    const y = h - ((p.cum_r - minY) / rangeY) * h;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const zeroY = h - ((0 - minY) / rangeY) * h;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" data-testid="equity-curve">
      <line x1="0" y1={zeroY} x2={w} y2={zeroY} stroke="#334155" strokeDasharray="3 3" />
      <path d={path} fill="none" stroke="#06B6D4" strokeWidth="1.5" />
      <text x="4" y="12" fontSize="10" fill="#64748B" fontFamily="JetBrains Mono">
        Cum R (5D returns): {ys[ys.length - 1].toFixed(2)}%
      </text>
    </svg>
  );
}

const ORIGIN_TABS = ["BACKTESTED", "LIVE"];
const INTRADAY_ORIGIN_TABS = ["BACKTESTED_INTRADAY", "LIVE_INTRADAY"];
const MODE_TABS = [
  { key: "SWING", label: "Swing" },
  { key: "INTRADAY", label: "Intraday" },
];

function EdgeBanner({ edge, populationSize }) {
  if (!edge) return null;
  const map = {
    POSITIVE_EDGE: "border-emerald-500/60 bg-emerald-500/15 text-emerald-200",
    NEGATIVE_EDGE: "border-red-500/60 bg-red-500/15 text-red-200",
    MARGINAL:      "border-amber-500/60 bg-amber-500/15 text-amber-200",
    INSUFFICIENT_SAMPLE: "border-slate-600 bg-slate-800/50 text-slate-200",
    UNKNOWN:       "border-slate-600 bg-slate-800/50 text-slate-300",
  };
  const cls = map[edge.verdict] || map.UNKNOWN;
  return (
    <div data-testid="intraday-edge-banner" data-verdict={edge.verdict} className={`border ${cls} px-4 py-3 mb-3`}>
      <div className="mono text-[10px] uppercase tracking-widest opacity-70">Intraday Edge Report · In-sample descriptive</div>
      <div className="mono text-lg font-extrabold tracking-tight mt-0.5">{edge.verdict.replaceAll("_", " ")}</div>
      <div className="mono text-[11px] mt-1">{edge.human}</div>
      <div className="mono text-[10px] mt-1 opacity-60 uppercase tracking-widest">
        Population: {populationSize} · Descriptive only — see I9 validation banner above.
      </div>
    </div>
  );
}

function PromotedConfigSection({ activeConfig }) {
  if (!activeConfig?.active) return null;
  const a = activeConfig.active;
  if (!a.config) {
    return (
      <div className="border border-[#1E293B] bg-[#0F1626] px-3 py-2 mb-3" data-testid="promoted-config-section">
        <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">Current Engine Version</div>
        <div className="mono text-sm text-cyan-300 font-bold">{a.version} <span className="text-slate-500 font-normal">· v1 baseline (no config promoted)</span></div>
      </div>
    );
  }
  // Look up validation metrics from the matching history row
  const hist = (activeConfig.history || []).find((h) => h.version === a.version);
  const vm = hist?.validation_metrics || {};
  const cfg = a.config;
  const activeFilters = [];
  if (cfg.threshold > 60) activeFilters.push(`threshold≥${cfg.threshold}`);
  if (cfg.regime_filter && cfg.regime_filter !== "any") activeFilters.push(`regime=${cfg.regime_filter}`);
  if (cfg.hour_filter && cfg.hour_filter !== "any") activeFilters.push(`hour=${cfg.hour_filter}`);
  if (cfg.direction_filter && cfg.direction_filter !== "both") activeFilters.push(`direction=${cfg.direction_filter}`);
  if ((cfg.min_rr || 0) > 0) activeFilters.push(`min_rr=${cfg.min_rr}`);
  if ((cfg.excluded_tickers || []).length) activeFilters.push(`exclude=${cfg.excluded_tickers.join(",")}`);
  return (
    <div className="border border-cyan-500/40 bg-cyan-500/[0.04] px-3 py-3 mb-3" data-testid="promoted-config-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-300">Promoted Config (Live)</div>
          <div className="mono text-sm text-cyan-100 font-bold mt-0.5">{a.version}</div>
        </div>
        <div className="mono text-[10px] text-slate-500">
          Promoted {new Date(a.promoted_at).toLocaleString()} by {a.promoted_by}
        </div>
      </div>
      <div className="mono text-[11px] text-slate-300 mb-2">
        Filters: {activeFilters.length ? activeFilters.join(" · ") : "none"}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2 border-t border-cyan-500/20">
        <Stat label="VAL Signals" value={vm.signal_count ?? "—"} testid="promoted-val-n" />
        <Stat label="VAL Win Rate" value={fmtPct(vm.win_rate_pct)} tone="cyan" testid="promoted-val-wr" />
        <Stat label="VAL PF" value={fmt(vm.profit_factor)} tone={(vm.profit_factor ?? 0) >= 1.05 ? "emerald" : "amber"} testid="promoted-val-pf" />
        <Stat label="VAL Expectancy" value={vm.expectancy_r != null ? `${fmt(vm.expectancy_r, 3)}R` : "—"} tone={(vm.expectancy_r ?? 0) > 0 ? "emerald" : "red"} testid="promoted-val-exp" />
        <Stat label="VAL Signals/Day" value={fmt(vm.signals_per_day, 2)} tone="slate" testid="promoted-val-spd" />
      </div>
      <div className="mono text-[10px] text-slate-500 mt-2 uppercase tracking-widest">In-sample / backfill-derived · compare to v1 baseline below</div>
    </div>
  );
}

function IntradayView({ origin, data, loading, status, onRefresh, onRunBackfill, busy, activeConfig }) {
  if (loading) {
    return (
      <div className="p-8 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Aggregating intraday results…
      </div>
    );
  }
  if (!data) return null;
  const o = data.overall || {};
  return (
    <>
      <EdgeBanner edge={data.edge} populationSize={data.population_size} />
      <PromotedConfigSection activeConfig={activeConfig} />
      <div className="border border-amber-500/30 bg-amber-500/10 px-3 py-2 mb-3 mono text-[11px] text-amber-200">
        {data.in_sample_banner}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
        <Stat label="Signals" value={o.signal_count ?? 0} testid="intra-stat-signals" />
        <Stat label="Filled" value={o.filled_count ?? 0} />
        <Stat label="Fill Rate" value={fmtPct(o.fill_rate_pct)} tone="cyan" testid="intra-stat-fill-rate" />
        <Stat label="Win Rate" value={fmtPct(o.win_rate_pct)} tone={o.win_rate_pct >= 55 ? "emerald" : o.win_rate_pct >= 45 ? "cyan" : "red"} testid="intra-stat-win-rate" />
        <Stat label="Profit Factor" value={fmt(o.profit_factor)} tone={(o.profit_factor ?? 0) >= 1.3 ? "emerald" : "amber"} testid="intra-stat-pf" />
        <Stat label="Expectancy R" value={o.expectancy_r != null ? `${fmt(o.expectancy_r, 3)}R` : "—"} tone={(o.expectancy_r ?? 0) >= 0 ? "emerald" : "red"} testid="intra-stat-exp" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <IntradayBucketTable title="By Hour of Day" data={data.by_hour_of_day} testid="intra-by-hour" />
        <IntradayBucketTable title="By Direction"    data={data.by_direction}    testid="intra-by-direction" />
        <IntradayBucketTable title="By Score Band"  data={data.by_score_band}   testid="intra-by-band" />
        <IntradayBucketTable title="By Day Regime"  data={data.by_day_regime}   testid="intra-by-regime" />
      </div>

      <IntradayBucketTable title="By Ticker" data={data.by_ticker} testid="intra-by-ticker" wide />

      <div className="border border-[#1E293B] bg-[#0F1626] mt-3">
        <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Equity Curve · Cumulative R</span>
        </div>
        <div className="p-3">
          {(!data.equity_curve || data.equity_curve.length < 2) ? (
            <div className="mono text-[11px] text-slate-500 p-4 text-center">Not enough matured signals yet.</div>
          ) : (
            <svg viewBox="0 0 800 160" className="w-full h-40" data-testid="intraday-equity-curve">
              {(() => {
                const pts = data.equity_curve;
                const ys = pts.map(p => p.cum_r);
                const minY = Math.min(...ys, 0);
                const maxY = Math.max(...ys, 0);
                const rY = maxY - minY || 1;
                const rX = (pts.length - 1) || 1;
                const path = pts.map((p, i) => `${i===0?"M":"L"}${((i/rX)*800).toFixed(1)},${(160-((p.cum_r-minY)/rY)*160).toFixed(1)}`).join(" ");
                const zeroY = 160 - ((0 - minY) / rY) * 160;
                return (<>
                  <line x1="0" y1={zeroY} x2="800" y2={zeroY} stroke="#334155" strokeDasharray="3 3" />
                  <path d={path} fill="none" stroke="#F59E0B" strokeWidth="1.5" />
                  <text x="4" y="12" fontSize="10" fill="#64748B" fontFamily="JetBrains Mono">
                    Cum R: {ys[ys.length-1].toFixed(2)}R
                  </text>
                </>);
              })()}
            </svg>
          )}
        </div>
      </div>
    </>
  );
}

function IntradayBucketTable({ title, data, testid, wide }) {
  const entries = Object.entries(data || {});
  entries.sort((a, b) => (b[1].signal_count || 0) - (a[1].signal_count || 0));
  return (
    <div className="border border-[#1E293B] bg-[#0F1626]" data-testid={testid}>
      <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
        <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{title}</span>
      </div>
      <div className="p-3">
        {entries.length === 0 ? (
          <div className="mono text-[11px] text-slate-500 text-center py-2">No data.</div>
        ) : (
          <table className="q-table">
            <thead><tr><th>{title.split(" ").slice(1).join(" ") || "Bucket"}</th><th className="right">n</th><th className="right">Fill %</th><th className="right">Win %</th><th className="right">PF</th><th className="right">Exp R</th></tr></thead>
            <tbody>
              {entries.slice(0, wide ? 30 : 10).map(([k, s]) => (
                <tr key={k}>
                  <td className="text-slate-100">{k}</td>
                  <td className="right">{s.signal_count ?? 0}</td>
                  <td className="right text-cyan-300">{fmtPct(s.fill_rate_pct)}</td>
                  <td className={"right " + dirCls((s.win_rate_pct ?? 50) - 50)}>{fmtPct(s.win_rate_pct)}</td>
                  <td className="right text-slate-200">{fmt(s.profit_factor)}</td>
                  <td className={"right " + dirCls(s.expectancy_r)}>{s.expectancy_r != null ? `${fmt(s.expectancy_r, 3)}R` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [openSearch, setOpenSearch] = useState(false);
  const validation = useValidationStatus();
  const { style } = useTradingStyle() || {};
  const [mode, setMode] = useState(style === "INTRADAY" ? "INTRADAY" : "SWING");
  useEffect(() => { setMode(style === "INTRADAY" ? "INTRADAY" : "SWING"); }, [style]);
  const [data, setData] = useState(null);
  const [intradayData, setIntradayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [origin, setOrigin] = useState("BACKTESTED");
  const [intradayOrigin, setIntradayOrigin] = useState("BACKTESTED_INTRADAY");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [intradayStatus, setIntradayStatus] = useState(null);
  const [activeConfig, setActiveConfig] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchAll = () => {
    setLoading(true);
    const reqId = ++fetchIdRef.current;
    Promise.all([
      api.get("/signal/analytics"),
      api.get("/signal/backfill/status"),
      api.get("/intraday/analytics", { params: { origin: intradayOrigin } }),
      api.get("/intraday/backfill/status"),
      api.get("/intraday/lab/config").catch(() => ({ data: null })),
    ]).then(([a, b, c, d, e]) => {
      if (reqId !== fetchIdRef.current) return;
      setData(a.data);
      setStatus(b.data);
      setIntradayData(c.data);
      setIntradayStatus(d.data);
      setActiveConfig(e.data);
    }).finally(() => {
      if (reqId === fetchIdRef.current) setLoading(false);
    });
  };

  useEffect(() => { fetchAll(); }, [intradayOrigin]);

  const startBackfill = async () => {
    setBusy(true);
    try {
      if (mode === "INTRADAY") {
        await api.post("/intraday/backfill/start?max_sessions=60");
        const poll = async () => {
          const r = await api.get("/intraday/backfill/status");
          setIntradayStatus(r.data);
          if (r.data.status === "running") setTimeout(poll, 5000);
          else fetchAll();
        };
        setTimeout(poll, 1500);
      } else {
        await api.post("/signal/backfill/start", { step_days: 5 });
        const poll = async () => {
          const r = await api.get("/signal/backfill/status");
          setStatus(r.data);
          if (r.data.status === "running") setTimeout(poll, 3000);
          else fetchAll();
        };
        setTimeout(poll, 1500);
      }
    } finally { setBusy(false); }
  };

  const current = data ? data[origin] : null;
  const overall = current?.overall || {};
  const byBand = current?.by_score_band || {};
  const byDir = current?.by_direction || {};
  const bySector = current?.by_sector || {};

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpenSearch(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <BarChart3 className="h-4 w-4 text-cyan-400" />
            <h1 className="mono text-lg font-bold text-slate-100 tracking-tight uppercase">Performance Analytics</h1>
            {validation && (
              <div className="flex items-center gap-1.5 ml-2" data-testid="validation-chip-row">
                <ValidationChip status={validation.swing} testid="validation-chip-swing" />
                <ValidationChip status={validation.intraday} testid="validation-chip-intraday" />
              </div>
            )}
            <div className="flex items-center gap-1 ml-2" data-testid="mode-tabs">
              {MODE_TABS.map((m) => (
                <button
                  key={m.key}
                  data-testid={`mode-tab-${m.key}`}
                  onClick={() => setMode(m.key)}
                  className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${mode === m.key ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-[#1E293B] text-slate-400 hover:text-slate-200"}`}
                >{m.label}</button>
              ))}
            </div>
            {mode === "SWING" && (
              <div className="flex items-center gap-1 ml-4" data-testid="origin-tabs">
                {ORIGIN_TABS.map((o) => (
                  <button
                    key={o}
                    data-testid={`origin-tab-${o}`}
                    onClick={() => setOrigin(o)}
                    className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${origin === o ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200" : "border-[#1E293B] text-slate-400 hover:text-slate-200"}`}
                  >{o}</button>
                ))}
              </div>
            )}
            {mode === "INTRADAY" && (
              <div className="flex items-center gap-1 ml-4" data-testid="intraday-origin-tabs">
                {INTRADAY_ORIGIN_TABS.map((o) => (
                  <button
                    key={o}
                    data-testid={`intraday-origin-tab-${o}`}
                    onClick={() => setIntradayOrigin(o)}
                    className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${intradayOrigin === o ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-[#1E293B] text-slate-400 hover:text-slate-200"}`}
                  >{o.replace("_INTRADAY", "")}</button>
                ))}
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              <span data-testid="backfill-status" className="mono text-[10px] uppercase tracking-widest text-slate-500">
                {mode === "SWING"
                  ? `Backfill: ${status?.status || "—"} · created ${status?.created_snapshots ?? 0}`
                  : `Intraday BF: ${intradayStatus?.status || "—"} · signals ${intradayStatus?.totals?.signals ?? 0} · outcomes ${intradayStatus?.totals?.outcomes ?? 0}`}
              </span>
              <button
                data-testid="run-backfill"
                onClick={startBackfill}
                disabled={busy || (mode === "SWING" ? status?.status === "running" : intradayStatus?.status === "running")}
                className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 disabled:opacity-50 flex items-center gap-1"
              >
                {(mode === "SWING" ? status : intradayStatus)?.status === "running" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                Run {mode === "INTRADAY" ? "Intraday " : ""}Backfill
              </button>
              <button data-testid="refresh-analytics" onClick={fetchAll} className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#1E293B] text-slate-400 hover:text-slate-200 flex items-center gap-1">
                <RefreshCw className="h-3 w-3" /> Refresh
              </button>
            </div>
          </div>

          {mode === "INTRADAY" && (
            <>
              {validation?.intraday && (
                <ValidationBanner status={validation.intraday} testid="analytics-validation-banner-intraday" className="mb-3" />
              )}
              <IntradayView origin={intradayOrigin} data={intradayData} loading={loading} status={intradayStatus} onRefresh={fetchAll} onRunBackfill={startBackfill} busy={busy} activeConfig={activeConfig} />
            </>
          )}

          {mode === "SWING" && validation?.swing && (
            <ValidationBanner status={validation.swing} testid="analytics-validation-banner-swing" className="mb-3" />
          )}

          {mode === "SWING" && origin === "BACKTESTED" && (
            <div className="border border-amber-500/30 bg-amber-500/10 px-3 py-2 mb-3 mono text-[11px] text-amber-200" data-testid="backtest-honesty">
              Backtested statistics are IN-SAMPLE — the same engine produced and evaluated these signals. Treat as an internal calibration check, not out-of-sample performance.
            </div>
          )}

          {mode === "SWING" && loading && (
            <div className="p-8 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Aggregating results…
            </div>
          )}

          {mode === "SWING" && !loading && current && (
            <></>
          )}
          {mode === "SWING" && !loading && current && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-3">
                <Stat label="Total" value={overall.total ?? 0} testid="stat-total" />
                <Stat label="Decided" value={overall.decided ?? 0} testid="stat-decided" />
                <Stat label="Win Rate" value={fmtPct(overall.win_rate_pct)} tone={overall.win_rate_pct >= 55 ? "emerald" : overall.win_rate_pct >= 45 ? "cyan" : "red"} testid="stat-win-rate" />
                <Stat label="Avg Winner" value={fmtPct(overall.avg_winner_pct)} tone="emerald" />
                <Stat label="Avg Loser" value={fmtPct(overall.avg_loser_pct)} tone="red" />
                <Stat label="Profit Factor" value={fmt(overall.profit_factor)} tone={overall.profit_factor >= 1.3 ? "emerald" : "amber"} testid="stat-pf" />
                <Stat label="Expectancy" value={fmtPct(overall.expectancy_pct)} tone={overall.expectancy_pct >= 0 ? "emerald" : "red"} testid="stat-expectancy" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
                <div className="border border-[#1E293B] bg-[#0F1626]">
                  <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
                    <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Calibration by Score Band</span>
                  </div>
                  <div className="p-3">
                    <table className="q-table" data-testid="calibration-table">
                      <thead><tr><th>Band</th><th className="right">n</th><th className="right">Win Rate</th><th className="right">Avg Winner</th><th className="right">Avg Loser</th><th className="right">PF</th></tr></thead>
                      <tbody>
                        {["60-69","70-79","80-89","90-100"].map((b) => {
                          const s = byBand[b] || {};
                          return (
                            <tr key={b}>
                              <td className="text-slate-100">{b}</td>
                              <td className="right">{s.total ?? 0}</td>
                              <td className={"right " + dirCls((s.win_rate_pct ?? 50) - 50)}>{fmtPct(s.win_rate_pct)}</td>
                              <td className="right text-emerald-300">{fmtPct(s.avg_winner_pct)}</td>
                              <td className="right text-red-300">{fmtPct(s.avg_loser_pct)}</td>
                              <td className="right text-slate-200">{fmt(s.profit_factor)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border border-[#1E293B] bg-[#0F1626]">
                  <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
                    <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Long vs Short</span>
                  </div>
                  <div className="p-3">
                    <table className="q-table">
                      <thead><tr><th>Dir</th><th className="right">n</th><th className="right">Win Rate</th><th className="right">Avg Winner</th><th className="right">Avg Loser</th><th className="right">PF</th></tr></thead>
                      <tbody>
                        {["LONG","SHORT"].map((d) => {
                          const s = byDir[d] || {};
                          return (
                            <tr key={d}>
                              <td className="text-slate-100">{d}</td>
                              <td className="right">{s.total ?? 0}</td>
                              <td className={"right " + dirCls((s.win_rate_pct ?? 50) - 50)}>{fmtPct(s.win_rate_pct)}</td>
                              <td className="right text-emerald-300">{fmtPct(s.avg_winner_pct)}</td>
                              <td className="right text-red-300">{fmtPct(s.avg_loser_pct)}</td>
                              <td className="right text-slate-200">{fmt(s.profit_factor)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="border border-[#1E293B] bg-[#0F1626] mb-3">
                <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
                  <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Equity Curve · Cumulative R (5D returns)</span>
                </div>
                <div className="p-3">
                  <EquityCurve points={current.equity_curve || []} />
                </div>
              </div>

              <div className="border border-[#1E293B] bg-[#0F1626] mb-3">
                <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
                  <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">By Market Regime</span>
                </div>
                <div className="p-3">
                  <table className="q-table" data-testid="by-regime-table">
                    <thead><tr><th>Regime</th><th className="right">n</th><th className="right">Win Rate</th><th className="right">Avg Winner</th><th className="right">Avg Loser</th><th className="right">PF</th></tr></thead>
                    <tbody>
                      {["STRONG_RISK_ON","RISK_ON","NEUTRAL","RISK_OFF","STRONG_RISK_OFF"].map((k) => {
                        const s = (current.by_regime || {})[k] || {};
                        return (
                          <tr key={k}>
                            <td className="text-slate-100">{k.replaceAll("_", " ")}</td>
                            <td className="right">{s.total ?? 0}</td>
                            <td className={"right " + dirCls((s.win_rate_pct ?? 50) - 50)}>{fmtPct(s.win_rate_pct)}</td>
                            <td className="right text-emerald-300">{fmtPct(s.avg_winner_pct)}</td>
                            <td className="right text-red-300">{fmtPct(s.avg_loser_pct)}</td>
                            <td className="right text-slate-200">{fmt(s.profit_factor)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="border border-[#1E293B] bg-[#0F1626]">
                <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
                  <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">By Sector ETF</span>
                </div>
                <div className="p-3">
                  <table className="q-table">
                    <thead><tr><th>Sector</th><th className="right">n</th><th className="right">Win Rate</th><th className="right">PF</th><th className="right">Expectancy</th></tr></thead>
                    <tbody>
                      {Object.entries(bySector).sort((a, b) => (b[1].total || 0) - (a[1].total || 0)).map(([k, s]) => (
                        <tr key={k}>
                          <td className="text-slate-100">{k}</td>
                          <td className="right">{s.total ?? 0}</td>
                          <td className={"right " + dirCls((s.win_rate_pct ?? 50) - 50)}>{fmtPct(s.win_rate_pct)}</td>
                          <td className="right text-slate-200">{fmt(s.profit_factor)}</td>
                          <td className={"right " + dirCls(s.expectancy_pct)}>{fmtPct(s.expectancy_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <TickerSearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <DisclaimerFooter />
    </div>
  );
}
